import React, { useState } from 'react';
import { Button } from './Button';
import { FormField } from './FormField';
import { CheckCircleIcon } from 'lucide-react';
interface ContractFormProps {
  brandName: string;
  organizerName: string;
  eventName: string;
  matchId: string;
  onContractCreated: (contractData: any) => void;
}
export function ContractForm({
  brandName,
  organizerName,
  eventName,
  matchId,
  onContractCreated
}: ContractFormProps) {
  const [formData, setFormData] = useState({
    sponsorshipAmount: '',
    sponsorshipType: '',
    deliverables: '',
    startDate: '',
    endDate: '',
    paymentTerms: '',
    cancellationPolicy: '',
    additionalTerms: ''
  });
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // In a real app, this would send data to a server
    setTimeout(() => {
      const contractData = {
        id: `contract-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        matchId,
        brandName,
        organizerName,
        eventName,
        ...formData,
        createdAt: new Date(),
        status: 'pending'
      };
      // Save to localStorage
      const contracts = JSON.parse(localStorage.getItem('contracts') || '[]');
      contracts.push(contractData);
      localStorage.setItem('contracts', JSON.stringify(contracts));
      onContractCreated(contractData);
      setIsSubmitting(false);
    }, 1000);
  };
  const nextStep = () => setStep(step + 1);
  const prevStep = () => setStep(step - 1);
  const sponsorshipTypeOptions = [{
    value: 'financial',
    label: 'Finansiell sponsring'
  }, {
    value: 'product',
    label: 'Produktsponsring'
  }, {
    value: 'service',
    label: 'Tjänstesponsring'
  }, {
    value: 'mixed',
    label: 'Blandad sponsring'
  }];
  const paymentTermsOptions = [{
    value: 'full_upfront',
    label: 'Full betalning i förskott'
  }, {
    value: 'installments',
    label: '50% i förskott, 50% efter eventet'
  }, {
    value: 'post_event',
    label: 'Full betalning efter eventet'
  }, {
    value: 'custom',
    label: 'Anpassad betalningsplan'
  }];
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-212">
      <h2 className="text-xl font-semibold text-gray-900 mb-4" data-id="element-213">
        Skapa sponsringsavtal
      </h2>
      <p className="text-gray-600 mb-6" data-id="element-214">
        Detta avtal kommer att formalisera samarbetet mellan {brandName} och{' '}
        {organizerName} för eventet "{eventName}".
      </p>
      {step === 1 && <div data-id="element-215">
          <div className="space-y-4 mb-6" data-id="element-216">
            <FormField label="Sponsringsbelopp (SEK)" id="sponsorshipAmount" required value={formData.sponsorshipAmount} onChange={handleChange} placeholder="t.ex. 25000" data-id="element-217" />
            <FormField label="Sponsringstyp" id="sponsorshipType" type="select" options={sponsorshipTypeOptions} required value={formData.sponsorshipType} onChange={handleChange} data-id="element-218" />
            <FormField label="Leverabler (vad varumärket får)" id="deliverables" textarea required placeholder="Lista alla leverabler som varumärket kommer att få, t.ex. logotyp på marknadsföringsmaterial, monterplats, etc." value={formData.deliverables} onChange={handleChange} data-id="element-219" />
          </div>
          <div className="flex justify-end" data-id="element-220">
            <Button onClick={nextStep} variant="primary" data-id="element-221">
              Fortsätt
            </Button>
          </div>
        </div>}
      {step === 2 && <form onSubmit={handleSubmit} data-id="element-222">
          <div className="space-y-4 mb-6" data-id="element-223">
            <div className="grid grid-cols-2 gap-4" data-id="element-224">
              <FormField label="Startdatum" id="startDate" type="date" required value={formData.startDate} onChange={handleChange} data-id="element-225" />
              <FormField label="Slutdatum" id="endDate" type="date" required value={formData.endDate} onChange={handleChange} data-id="element-226" />
            </div>
            <FormField label="Betalningsvillkor" id="paymentTerms" type="select" options={paymentTermsOptions} required value={formData.paymentTerms} onChange={handleChange} data-id="element-227" />
            <FormField label="Avbokningspolicy" id="cancellationPolicy" textarea required placeholder="Beskriv villkoren för avbokning, t.ex. '50% återbetalning vid avbokning 30 dagar före eventet'" value={formData.cancellationPolicy} onChange={handleChange} data-id="element-228" />
            <FormField label="Ytterligare villkor (valfritt)" id="additionalTerms" textarea placeholder="Lägg till eventuella ytterligare villkor eller noteringar" value={formData.additionalTerms} onChange={handleChange} data-id="element-229" />
          </div>
          <div className="flex justify-between" data-id="element-230">
            <Button onClick={prevStep} variant="outline" data-id="element-231">
              Tillbaka
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} data-id="element-232">
              {isSubmitting ? 'Skapar avtal...' : 'Skapa avtal'}
            </Button>
          </div>
        </form>}
    </div>;
}
export function ContractDetails({
  contract
}: {
  contract: any;
}) {
  const [brandApproved, setBrandApproved] = useState(contract.brandApproved || false);
  const [organizerApproved, setOrganizerApproved] = useState(contract.organizerApproved || false);
  const handleApprove = (party: 'brand' | 'organizer') => {
    // In a real app, this would update the server
    const contracts = JSON.parse(localStorage.getItem('contracts') || '[]');
    const updatedContracts = contracts.map((c: any) => {
      if (c.id === contract.id) {
        return {
          ...c,
          brandApproved: party === 'brand' ? true : c.brandApproved,
          organizerApproved: party === 'organizer' ? true : c.organizerApproved,
          status: c.brandApproved && c.organizerApproved ? 'approved' : 'pending'
        };
      }
      return c;
    });
    localStorage.setItem('contracts', JSON.stringify(updatedContracts));
    if (party === 'brand') {
      setBrandApproved(true);
    } else {
      setOrganizerApproved(true);
    }
  };
  const isFullyApproved = brandApproved && organizerApproved;
  return <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-233">
      <div className="flex justify-between items-center mb-4" data-id="element-234">
        <h2 className="text-xl font-semibold text-gray-900" data-id="element-235">Sponsringsavtal</h2>
        <div className={`px-3 py-1 text-sm font-medium rounded-full ${isFullyApproved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-236">
          {isFullyApproved ? 'Godkänt' : 'Väntar på godkännande'}
        </div>
      </div>
      {isFullyApproved && <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6" data-id="element-237">
          <div className="flex items-start" data-id="element-238">
            <div className="flex-shrink-0" data-id="element-239">
              <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-240" />
            </div>
            <div className="ml-3" data-id="element-241">
              <h3 className="text-sm font-medium text-green-800" data-id="element-242">
                Avtalet är godkänt av båda parter
              </h3>
              <div className="mt-2 text-sm text-green-700" data-id="element-243">
                <p data-id="element-244">
                  Grattis! Avtalet mellan {contract.brandName} och{' '}
                  {contract.organizerName} är nu officiellt.
                </p>
              </div>
            </div>
          </div>
        </div>}
      <div className="space-y-6" data-id="element-245">
        <div className="grid grid-cols-2 gap-4" data-id="element-246">
          <div data-id="element-247">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-248">Varumärke</h3>
            <p className="text-gray-900" data-id="element-249">{contract.brandName}</p>
          </div>
          <div data-id="element-250">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-251">Arrangör</h3>
            <p className="text-gray-900" data-id="element-252">{contract.organizerName}</p>
          </div>
        </div>
        <div data-id="element-253">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-254">Event</h3>
          <p className="text-gray-900" data-id="element-255">{contract.eventName}</p>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-256">
          <div data-id="element-257">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-258">
              Sponsringsbelopp
            </h3>
            <p className="text-gray-900" data-id="element-259">{contract.sponsorshipAmount} SEK</p>
          </div>
          <div data-id="element-260">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-261">Sponsringstyp</h3>
            <p className="text-gray-900" data-id="element-262">
              {contract.sponsorshipType === 'financial' ? 'Finansiell sponsring' : contract.sponsorshipType === 'product' ? 'Produktsponsring' : contract.sponsorshipType === 'service' ? 'Tjänstesponsring' : 'Blandad sponsring'}
            </p>
          </div>
        </div>
        <div data-id="element-263">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-264">Leverabler</h3>
          <p className="text-gray-900 whitespace-pre-line" data-id="element-265">
            {contract.deliverables}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4" data-id="element-266">
          <div data-id="element-267">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-268">Startdatum</h3>
            <p className="text-gray-900" data-id="element-269">
              {new Date(contract.startDate).toLocaleDateString()}
            </p>
          </div>
          <div data-id="element-270">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-271">Slutdatum</h3>
            <p className="text-gray-900" data-id="element-272">
              {new Date(contract.endDate).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div data-id="element-273">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-274">
            Betalningsvillkor
          </h3>
          <p className="text-gray-900" data-id="element-275">
            {contract.paymentTerms === 'full_upfront' ? 'Full betalning i förskott' : contract.paymentTerms === 'installments' ? '50% i förskott, 50% efter eventet' : contract.paymentTerms === 'post_event' ? 'Full betalning efter eventet' : 'Anpassad betalningsplan'}
          </p>
        </div>
        <div data-id="element-276">
          <h3 className="text-sm font-medium text-gray-500" data-id="element-277">
            Avbokningspolicy
          </h3>
          <p className="text-gray-900" data-id="element-278">{contract.cancellationPolicy}</p>
        </div>
        {contract.additionalTerms && <div data-id="element-279">
            <h3 className="text-sm font-medium text-gray-500" data-id="element-280">
              Ytterligare villkor
            </h3>
            <p className="text-gray-900" data-id="element-281">{contract.additionalTerms}</p>
          </div>}
        <div className="border-t pt-4 mt-6" data-id="element-282">
          <div className="flex justify-between" data-id="element-283">
            <div data-id="element-284">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-285">
                Varumärkets godkännande
              </h3>
              {brandApproved ? <div className="flex items-center text-green-600" data-id="element-286">
                  <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-287" />
                  <span data-id="element-288">Godkänt</span>
                </div> : <Button variant="outline" onClick={() => handleApprove('brand')} className="mt-2" data-id="element-289">
                  Godkänn som varumärke
                </Button>}
            </div>
            <div data-id="element-290">
              <h3 className="text-sm font-medium text-gray-500" data-id="element-291">
                Arrangörens godkännande
              </h3>
              {organizerApproved ? <div className="flex items-center text-green-600" data-id="element-292">
                  <CheckCircleIcon className="h-4 w-4 mr-1" data-id="element-293" />
                  <span data-id="element-294">Godkänt</span>
                </div> : <Button variant="outline" onClick={() => handleApprove('organizer')} className="mt-2" data-id="element-295">
                  Godkänn som arrangör
                </Button>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}